/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import type { ShopperSearch } from '@/scapi';
import PriceRangeInput from '@/components/price-range-input';
import DefaultRefinement from '../refine-default';
import type { RefinementProps } from '../types';

export default function RefinePrice({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
    result,
}: RefinementProps & {
    result?: ShopperSearch.schemas['ProductSearchResult'];
}): ReactElement {
    const location = useLocation();
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const isInitialMount = useRef(true);

    // Populate inputs from URL on location change (skip on initial mount/reload)
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        const params = new URLSearchParams(location.search);
        const refines = params.getAll('refine');

        // Find price refinement in URL
        const priceRefine = refines.find((refine) => refine.startsWith('price='));
        if (priceRefine) {
            // Extract price range from format: price=(min..max)
            const match = priceRefine.match(/price=\(([^.]*)\.\.([^)]*)\)/);
            if (match) {
                const urlMin = match[1] || '';
                const urlMax = match[2] ? String(Number(match[2]) - 1) : '';
                setMinPrice(urlMin);
                setMaxPrice(urlMax);
            }
        } else {
            // Clear inputs if no price filter in URL
            setMinPrice('');
            setMaxPrice('');
        }
    }, [location.search]);

    // Get price limits from products for validation
    const { minAllowed, maxAllowed } = useMemo(() => {
        if (!result?.hits?.length) return { minAllowed: undefined, maxAllowed: undefined };
        const prices = result.hits
            .map((product) => product.price)
            .filter((price): price is number => typeof price === 'number' && price > 0);
        if (prices.length === 0) return { minAllowed: undefined, maxAllowed: undefined };
        return {
            minAllowed: Math.min(...prices),
            maxAllowed: Math.max(...prices),
        };
    }, [result]);

    const handlePriceChange = (newMinPrice: string, newMaxPrice: string) => {
        setMinPrice(newMinPrice);
        setMaxPrice(newMaxPrice);
    };

    const handleApplyPriceFilter = () => {
        if (minPrice || maxPrice) {
            const maxVal = maxPrice ? String(Math.ceil(Number(maxPrice)) + 1) : '';
            const priceRangeString = `(${minPrice || '0'}..${maxVal})`;
            toggleFilter(attributeId, priceRangeString);
        }
    };

    const sliderMin = 0;
    const sliderMax = Math.max(maxAllowed || 0, Number(maxPrice) || 0) || 1000;
    const currentMin = minPrice ? Number(minPrice) : sliderMin;
    const currentMax = maxPrice ? Number(maxPrice) : sliderMax;

    return (
        <div className="flex flex-col">
            {/* Min/Max Price Inputs */}
            <PriceRangeInput
                minPrice={minPrice}
                maxPrice={maxPrice}
                onChange={handlePriceChange}
                onApply={handleApplyPriceFilter}
                minAllowed={minAllowed}
                maxAllowed={maxAllowed}
                showValidationErrors={false}
            />

            {/* Range Slider */}
            <div className="price-slider-container">
                <div className="price-slider-track" />
                <div
                    className="price-slider-track"
                    style={{
                        left: `${((currentMin - sliderMin) / (sliderMax - sliderMin)) * 100}%`,
                        right: `${((sliderMax - currentMax) / (sliderMax - sliderMin)) * 100}%`,
                        backgroundColor: '#000000',
                    }}
                />
                <input
                    type="range"
                    min={sliderMin}
                    max={sliderMax}
                    value={currentMin}
                    onChange={(e) => {
                        const val = Math.min(Number(e.target.value), currentMax - 1);
                        handlePriceChange(String(val), String(currentMax));
                    }}
                    onMouseUp={handleApplyPriceFilter}
                    onTouchEnd={handleApplyPriceFilter}
                />
                <input
                    type="range"
                    min={sliderMin}
                    max={sliderMax}
                    value={currentMax}
                    onChange={(e) => {
                        const val = Math.max(Number(e.target.value), currentMin + 1);
                        handlePriceChange(String(currentMin), String(val));
                    }}
                    onMouseUp={handleApplyPriceFilter}
                    onTouchEnd={handleApplyPriceFilter}
                />
            </div>

            {/* Predefined Price Ranges */}
            <div className="price-segments-container">
                <DefaultRefinement
                    values={[
                        { value: '(0..500)', label: '₹0 - ₹500' },
                        { value: '(500..1000)', label: '₹500 - ₹1000' },
                    ]}
                    attributeId={attributeId}
                    isFilterSelected={isFilterSelected}
                    toggleFilter={toggleFilter}
                />
            </div>
        </div>
    );
}
